/**
 * Store Locator Section - Reia Diamonds
 *
 * Displays store locations across multiple cities with cascading selection:
 * City → Store → Details
 */

(function() {
  'use strict';

  // ============================================
  // Logger
  // ============================================
  const Logger = {
    enabled: false, // Set to true for debugging
    prefix: '[StoreLocator]',

    log(level, event, data = {}) {
      if (!this.enabled) return;

      const timestamp = new Date().toISOString();
      const logData = { timestamp, event, ...data };

      switch (level) {
        case 'error':
          console.error(this.prefix, event, logData);
          break;
        case 'warn':
          console.warn(this.prefix, event, logData);
          break;
        case 'debug':
          console.debug(this.prefix, event, logData);
          break;
        default:
          console.log(this.prefix, event, logData);
      }
    },

    info(event, data) { this.log('info', event, data); },
    warn(event, data) { this.log('warn', event, data); },
    error(event, data) { this.log('error', event, data); },
    debug(event, data) { this.log('debug', event, data); }
  };

  // ============================================
  // Constants
  // ============================================
  const MOBILE_BREAKPOINT = 768;
  const ANIMATION_LOCK_MS = 300;
  const SKELETON_DURATION_MS = 150;

  // ============================================
  // Utilities
  // ============================================
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // ============================================
  // Store Locator Module
  // ============================================
  const StoreLocator = {
    // State
    state: {
      cities: [],
      defaults: null,
      storeTypeLabels: null,
      amenitiesLabels: null,
      selectedCityId: null,
      selectedStoreId: null,
      isMobile: false,
      isLoading: true,
      isAnimating: false
    },

    // DOM References
    elements: {
      container: null,
      citiesContainer: null,
      citiesList: null,
      tabsContainer: null,
      detailsContainer: null,
      scrollArrows: null,
      scrollLeftBtn: null,
      scrollRightBtn: null,
      announcer: null
    },

    // ============================================
    // Initialization
    // ============================================
    init(container) {
      Logger.info('init-start', { timestamp: Date.now() });

      this.elements.container = container;
      this.elements.citiesContainer = container.querySelector('[data-cities-container]');
      this.elements.citiesList = container.querySelector('[data-cities-list]');
      this.elements.tabsContainer = container.querySelector('[data-tabs-container]');
      this.elements.detailsContainer = container.querySelector('[data-details-container]');
      this.elements.scrollArrows = container.querySelector('[data-scroll-arrows]');
      this.elements.scrollLeftBtn = container.querySelector('[data-scroll-left]');
      this.elements.scrollRightBtn = container.querySelector('[data-scroll-right]');
      this.elements.announcer = container.querySelector('[data-announcer]');

      this.checkMobile();

      this.loadData();
      this.bindEvents();

      Logger.info('init-complete', {
        timestamp: Date.now(),
        cityCount: this.state.cities.length,
        totalStores: this.state.cities.reduce((sum, city) => sum + city.stores.length, 0)
      });
    },

    // ============================================
    // Data Loading
    // ============================================
    loadData() {
      Logger.info('data-load-start', { source: 'json' });

      try {
        const dataAttr = this.elements.container.getAttribute('data-store-data');
        if (!dataAttr) {
          throw new Error('No store data found');
        }

        // Log first 100 chars for debugging
        Logger.debug('data-raw', { preview: dataAttr.substring(0, 100), length: dataAttr.length });

        let data;
        try {
          data = JSON.parse(dataAttr);
        } catch (parseError) {
          // Log the problematic area for debugging
          Logger.error('json-parse-error', {
            error: parseError.message,
            dataPreview: dataAttr.substring(0, 200),
            dataLength: dataAttr.length
          });
          throw parseError;
        }

        this.state.cities = data.cities || [];
        this.state.defaults = data.defaults || null;
        this.state.storeTypeLabels = data.store_type_labels || null;
        this.state.amenitiesLabels = data.amenities_labels || null;

        Logger.info('data-load-success', {
          cityCount: this.state.cities.length,
          hasDefaults: !!this.state.defaults,
          hasStoreTypeLabels: !!this.state.storeTypeLabels,
          hasAmenitiesLabels: !!this.state.amenitiesLabels
        });

        // Set default selections
        if (this.state.cities.length > 0) {
          const firstCity = this.state.cities[0];
          this.state.selectedCityId = firstCity.id;

          if (firstCity.stores.length > 0) {
            this.state.selectedStoreId = firstCity.stores[0].id;
          }
        }

        // Override defaults if URL hash targets a specific city/store
        this.applyHashSelection();

        this.state.isLoading = false;
        this.render();

        // Scroll to section if hash was present
        if (window.location.hash) {
          requestAnimationFrame(() => {
            this.elements.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }

      } catch (error) {
        Logger.error('data-load-error', { error: error.message });
        this.state.isLoading = false;
        this.renderError();
      }
    },

    // ============================================
    // Hash-based Deep Linking
    // ============================================

    // Map user-facing hashes and legacy variants to actual store IDs in the data.
    // The mega-nav uses #rs-puram but the data ID is "clock-tower".
    HASH_ALIASES: {
      'bangalore-jayanagar': 'jayanagar',
      'bengaluru-jayanagar': 'jayanagar',
      'bangalore-dickenson': 'dickenson-road',
      'bengaluru-dickenson-road': 'dickenson-road',
      'rs-puram': 'clock-tower',
      'coimbatore-rs-puram': 'clock-tower',
      'coimbatore-cross-cut': 'cross-cut-road',
      'coimbatore-cross-cut-road': 'cross-cut-road',
      'guwahati': 'fancy-bazaar',
      'guwahati-fancy-bazaar': 'fancy-bazaar'
    },

    applyHashSelection() {
      let hash = window.location.hash.replace('#', '').toLowerCase();
      if (!hash) return;

      // Resolve legacy aliases
      hash = this.HASH_ALIASES[hash] || hash;

      Logger.info('hash-navigation', { hash });

      // Match hash directly against store IDs
      for (const city of this.state.cities) {
        for (const store of city.stores) {
          if (store.id.toLowerCase() === hash) {
            this.state.selectedCityId = city.id;
            this.state.selectedStoreId = store.id;
            Logger.info('hash-match', { cityId: city.id, storeId: store.id });
            return;
          }
        }
      }

      // Fallback: match against city IDs
      for (const city of this.state.cities) {
        if (city.id.toLowerCase() === hash) {
          this.state.selectedCityId = city.id;
          if (city.stores.length > 0) {
            this.state.selectedStoreId = city.stores[0].id;
          }
          Logger.info('hash-match-city', { cityId: city.id });
          return;
        }
      }

      Logger.warn('hash-no-match', { hash });
    },

    // ============================================
    // Event Binding
    // ============================================
    bindEvents() {
      // Resize handler
      window.addEventListener('resize', debounce(() => this.handleResize(), 150));

      // Hash change handler (e.g. user clicks a link with #city-store)
      window.addEventListener('hashchange', () => {
        this.applyHashSelection();
        this.render();
        this.elements.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      // Scroll arrow buttons
      if (this.elements.scrollLeftBtn) {
        this.elements.scrollLeftBtn.addEventListener('click', () => this.scrollCities('left'));
      }
      if (this.elements.scrollRightBtn) {
        this.elements.scrollRightBtn.addEventListener('click', () => this.scrollCities('right'));
      }

      // Update scroll arrows on scroll
      if (this.elements.citiesContainer) {
        this.elements.citiesContainer.addEventListener('scroll', debounce(() => this.updateScrollArrows(), 50));
      }

      // Keyboard navigation
      this.elements.container.addEventListener('keydown', (e) => this.handleKeydown(e));

      // GTM CTA tracking
      this.elements.container.addEventListener('click', (e) => {
        const el = e.target.closest('[data-gtm-event]');
        if (!el) return;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: el.getAttribute('data-gtm-event') });
      });
    },

    // ============================================
    // Scroll Arrow Handling
    // ============================================
    scrollCities(direction) {
      const container = this.elements.citiesContainer;
      if (!container) return;

      const scrollAmount = 150;
      const currentScroll = container.scrollLeft;
      const newScroll = direction === 'left'
        ? currentScroll - scrollAmount
        : currentScroll + scrollAmount;

      container.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });

      Logger.debug('scroll-cities', { direction, from: currentScroll, to: newScroll });
    },

    updateScrollArrows() {
      if (!this.state.isMobile) return;

      const container = this.elements.citiesContainer;
      const leftBtn = this.elements.scrollLeftBtn;
      const rightBtn = this.elements.scrollRightBtn;

      if (!container || !leftBtn || !rightBtn) return;

      const scrollLeft = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;

      // Show/hide left arrow
      if (scrollLeft <= 10) {
        leftBtn.classList.add('store-locator__scroll-arrow--hidden');
      } else {
        leftBtn.classList.remove('store-locator__scroll-arrow--hidden');
      }

      // Show/hide right arrow
      if (scrollLeft >= maxScroll - 10) {
        rightBtn.classList.add('store-locator__scroll-arrow--hidden');
      } else {
        rightBtn.classList.remove('store-locator__scroll-arrow--hidden');
      }
    },

    // ============================================
    // Event Handlers
    // ============================================
    handleCityClick(cityId) {
      if (this.state.isAnimating) {
        Logger.debug('click-blocked', { reason: 'animation-in-progress' });
        return;
      }

      this.state.isAnimating = true;
      setTimeout(() => { this.state.isAnimating = false; }, ANIMATION_LOCK_MS);

      this.selectCity(cityId);
    },

    handleStoreClick(storeId) {
      if (this.state.isAnimating) {
        Logger.debug('click-blocked', { reason: 'animation-in-progress' });
        return;
      }

      this.state.isAnimating = true;
      setTimeout(() => { this.state.isAnimating = false; }, ANIMATION_LOCK_MS);

      this.selectStore(storeId);
    },

    handleResize() {
      const wasMobile = this.state.isMobile;
      this.checkMobile();

      if (wasMobile !== this.state.isMobile) {
        Logger.info('breakpoint-change', { isMobile: this.state.isMobile });
        this.render();
      }
    },

    handleKeydown(e) {
      // Handle Enter/Space on focusable elements
      if (e.key === 'Enter' || e.key === ' ') {
        const target = e.target;

        if (target.classList.contains('store-locator__city-card')) {
          e.preventDefault();
          this.handleCityClick(target.dataset.cityId);
        } else if (target.classList.contains('store-locator__tab')) {
          e.preventDefault();
          this.handleStoreClick(target.dataset.storeId);
        }
      }
    },

    // ============================================
    // State Management
    // ============================================
    checkMobile() {
      this.state.isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    },

    selectCity(cityId) {
      const previousCityId = this.state.selectedCityId;

      // Show skeleton during city switch (desktop only, and only if switching cities)
      const showSkeleton = !this.state.isMobile && previousCityId && previousCityId !== cityId;

      this.state.selectedCityId = cityId;

      // Select first store in the new city
      const city = this.state.cities.find(c => c.id === cityId);
      if (city && city.stores.length > 0) {
        this.state.selectedStoreId = city.stores[0].id;
      }

      Logger.info('city-select', { cityId, previousCityId, showSkeleton });

      if (showSkeleton) {
        this.showContentSkeleton();
        setTimeout(() => {
          this.hideContentSkeleton();
          this.renderTabs();
          this.renderDetails();
        }, SKELETON_DURATION_MS);
        // Update cities immediately
        this.renderCities();
      } else {
        this.render();
      }

      this.announce(`Selected ${city ? city.name : 'city'}`);

      // Auto-scroll selected city into view on mobile
      if (this.state.isMobile) {
        requestAnimationFrame(() => {
          this.scrollSelectedCityIntoView();
          this.scrollSelectedStoreIntoView();
          this.updateScrollArrows();
        });
      }
    },

    selectStore(storeId) {
      const previousStoreId = this.state.selectedStoreId;
      this.state.selectedStoreId = storeId;

      Logger.info('store-select', {
        storeId,
        previousStoreId,
        cityId: this.state.selectedCityId
      });

      this.renderDetails();
      this.updateTabsActiveState();

      const city = this.state.cities.find(c => c.id === this.state.selectedCityId);
      const store = city ? city.stores.find(s => s.id === storeId) : null;
      this.announce(`Selected ${store ? store.name : 'store'}`);

      // Auto-scroll selected store into view on mobile
      if (this.state.isMobile) {
        requestAnimationFrame(() => this.scrollSelectedStoreIntoView());
      }
    },

    // ============================================
    // Skeleton Loading
    // ============================================
    showContentSkeleton() {
      this.elements.container.classList.add('store-locator--content-loading');
      Logger.debug('skeleton-show');
    },

    hideContentSkeleton() {
      this.elements.container.classList.remove('store-locator--content-loading');
      Logger.debug('skeleton-hide');
    },

    // ============================================
    // Auto-scroll helpers (Mobile)
    // ============================================
    scrollSelectedCityIntoView() {
      const activeCity = this.elements.citiesList.querySelector('.store-locator__city-card--active');
      if (activeCity && this.elements.citiesContainer) {
        this.scrollElementIntoView(activeCity, this.elements.citiesContainer);
      }
    },

    scrollSelectedStoreIntoView() {
      const activeTab = this.elements.tabsContainer.querySelector('.store-locator__tab--active');
      if (activeTab) {
        this.scrollElementIntoView(activeTab, this.elements.tabsContainer);
      }
    },

    scrollElementIntoView(element, container) {
      const elementRect = element.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Calculate if element is outside visible area
      const isOutOfViewLeft = elementRect.left < containerRect.left;
      const isOutOfViewRight = elementRect.right > containerRect.right;

      if (isOutOfViewLeft || isOutOfViewRight) {
        // Scroll to center the element
        const scrollLeft = element.offsetLeft - (container.offsetWidth / 2) + (element.offsetWidth / 2);
        container.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: 'smooth'
        });

        Logger.debug('scroll-into-view', {
          element: element.className,
          scrollLeft
        });
      }
    },

    // ============================================
    // Rendering
    // ============================================
    render() {
      Logger.debug('render', { component: 'all', state: this.state });

      this.elements.container.classList.add('store-locator--loaded');
      this.renderCities();
      this.renderTabs();
      this.renderDetails();

      // Update scroll arrows on mobile
      if (this.state.isMobile) {
        requestAnimationFrame(() => {
          this.updateScrollArrows();
          this.scrollSelectedCityIntoView();
        });
      }
    },

    renderCities() {
      const citiesList = this.elements.citiesList;
      citiesList.innerHTML = '';

      this.state.cities.forEach(city => {
        const isActive = city.id === this.state.selectedCityId;
        const card = this.createCityCard(city, isActive);
        citiesList.appendChild(card);
      });
    },

    createCityCard(city, isActive) {
      const card = document.createElement('div');
      card.className = `store-locator__city-card${isActive ? ' store-locator__city-card--active' : ''}`;
      card.dataset.cityId = city.id;
      card.setAttribute('tabindex', '0');
      card.setAttribute('role', 'button');
      card.setAttribute('aria-pressed', isActive ? 'true' : 'false');

      const storeCount = city.stores.length;
      const storeText = storeCount === 1 ? '1 store' : `${storeCount} stores`;

      card.innerHTML = `
        <div class="store-locator__city-image-wrapper">
          <img
            class="store-locator__city-image"
            src="${escapeHtml(city.image)}"
            alt="${escapeHtml(city.name)}"
            loading="lazy"
          >
          <span class="store-locator__city-image-fallback" aria-hidden="true">${escapeHtml(city.name.charAt(0))}</span>
        </div>
        <span class="store-locator__city-name">${escapeHtml(city.name)}</span>
        <span class="store-locator__city-count">${storeText}</span>
        <span class="store-locator__city-chevron" aria-hidden="true"></span>
      `;

      // Add image error handling
      const img = card.querySelector('.store-locator__city-image');
      img.addEventListener('error', () => {
        Logger.warn('image-load-error', { cityId: city.id, src: city.image });
        card.classList.add('store-locator__city-card--image-error');
      });

      card.addEventListener('click', () => this.handleCityClick(city.id));

      return card;
    },

    renderTabs() {
      const tabsContainer = this.elements.tabsContainer;
      tabsContainer.innerHTML = '';

      const selectedCity = this.state.cities.find(c => c.id === this.state.selectedCityId);
      if (!selectedCity) return;

      // Add sliding indicator (desktop only)
      if (!this.state.isMobile) {
        const indicator = document.createElement('div');
        indicator.className = 'store-locator__tab-indicator';
        indicator.setAttribute('aria-hidden', 'true');
        tabsContainer.appendChild(indicator);
      }

      selectedCity.stores.forEach(store => {
        const isActive = store.id === this.state.selectedStoreId;
        const tab = this.createStoreTab(store, isActive);
        tabsContainer.appendChild(tab);
      });

      // Position indicator after tabs are rendered
      if (!this.state.isMobile) {
        requestAnimationFrame(() => this.updateTabIndicator(false));
      }
    },

    createStoreTab(store, isActive) {
      const tab = document.createElement('button');
      tab.className = `store-locator__tab${isActive ? ' store-locator__tab--active' : ''}`;
      tab.dataset.storeId = store.id;
      tab.setAttribute('aria-current', isActive ? 'true' : 'false');
      tab.textContent = store.name;

      tab.addEventListener('click', () => this.handleStoreClick(store.id));

      return tab;
    },

    updateTabsActiveState() {
      const tabs = this.elements.tabsContainer.querySelectorAll('.store-locator__tab');
      tabs.forEach(tab => {
        const isActive = tab.dataset.storeId === this.state.selectedStoreId;
        tab.classList.toggle('store-locator__tab--active', isActive);
        tab.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      // Animate indicator to new position
      if (!this.state.isMobile) {
        this.updateTabIndicator(true);
      }
    },

    updateTabIndicator(animate = true) {
      const indicator = this.elements.tabsContainer.querySelector('.store-locator__tab-indicator');
      const activeTab = this.elements.tabsContainer.querySelector('.store-locator__tab--active');

      if (!indicator || !activeTab) return;

      const tabsRect = this.elements.tabsContainer.getBoundingClientRect();
      const activeRect = activeTab.getBoundingClientRect();

      // Calculate position relative to tabs container
      const top = activeRect.top - tabsRect.top;
      const height = activeRect.height;

      // Apply transition class for animation
      if (animate) {
        indicator.classList.add('store-locator__tab-indicator--animating');
      } else {
        indicator.classList.remove('store-locator__tab-indicator--animating');
      }

      indicator.style.transform = `translateY(${top}px)`;
      indicator.style.height = `${height}px`;

      Logger.debug('indicator-update', { top, height, animate });
    },

    renderDetails() {
      const detailsContainer = this.elements.detailsContainer;
      const selectedCity = this.state.cities.find(c => c.id === this.state.selectedCityId);
      const selectedStore = selectedCity?.stores.find(s => s.id === this.state.selectedStoreId);

      if (!selectedStore) {
        detailsContainer.innerHTML = '<p>Select a store to view details</p>';
        return;
      }

      detailsContainer.innerHTML = this.createDetailsContent(selectedStore, selectedCity);
    },

    // SVG Icons
    icons: {
      location: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>`,
      landmark: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"></polygon></svg>`,
      email: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>`,
      phone: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>`,
      whatsapp: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>`,
      clock: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>`,
      calendar: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>`,
      map: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon><line x1="8" y1="2" x2="8" y2="18"></line><line x1="16" y1="6" x2="16" y2="22"></line></svg>`,
      check: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>`,
      fallback: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 8v4M12 16h.01"></path></svg>`
    },

    // Standardized Amenities with icons and labels
    amenityDefs: {
      parking: { icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 17V7h4a3 3 0 0 1 0 6H9"/></svg>`, label: 'Parking Available' },
      parking_assistance: { icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 17V7h4a3 3 0 0 1 0 6H9"/><path d="M17 17l2 2M19 17l-2 2" stroke-width="1.5"/></svg>`, label: 'Parking Assistance' },
      valet_parking: { icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 17h2l-2-7H5L3 17h2M7 17a2 2 0 1 0 4 0M13 17a2 2 0 1 0 4 0"/><path d="M5 10l2-5h10l2 5"/><circle cx="12" cy="5" r="1"/></svg>`, label: 'Valet Parking' },
      no_parking: { icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M4.93 4.93l14.14 14.14"/><path d="M9 15V9h4a2 2 0 0 1 0 4H9"/></svg>`, label: 'No Parking' },
      lift: { icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M12 6v4M10 8l2-2 2 2"/><path d="M12 14v4M10 16l2 2 2-2"/></svg>`, label: 'Lift Available' },
      no_lift: { icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 2L2 22"/><path d="M4 4v16h16"/><path d="M8 20V10l4 4 4-4v10"/></svg>`, label: 'Stairs Only' },
      ground_floor: { icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="10" rx="1"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/><circle cx="12" cy="16" r="1"/></svg>`, label: 'Ground Floor' },
      first_floor: { icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M9 11h6"/></svg>`, label: 'First Floor' },
      wheelchair: { icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="4" r="2"/><path d="M12 6v6"/><path d="M8 12h8l2 8H6"/><circle cx="8" cy="20" r="2"/><circle cx="16" cy="20" r="2"/></svg>`, label: 'Wheelchair Accessible' },
      private_viewing: { icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M12 8v4M12 16h.01"/></svg>`, label: 'Private Viewing' },
      lounge: { icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 12h16v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-6z"/><path d="M2 12a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2"/><path d="M6 12V8a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4"/></svg>`, label: 'Premium Lounge' },
      refreshments: { icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 8h1a4 4 0 1 1 0 8h-1"/><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>`, label: 'Refreshments' }
    },

    // ============================================
    // Helper: Get merged store data with defaults
    // ============================================
    getMergedStoreData(store) {
      const defaults = this.state.defaults || {};
      const defaultContact = defaults.contact || {};
      const defaultHours = defaults.hours || {};

      // Merge contact: store-level overrides defaults
      const contact = {
        email: store.contact?.email || store.email || defaultContact.email,
        phone: store.contact?.phone || store.phone || defaultContact.phone,
        whatsapp: store.contact?.whatsapp || store.whatsapp || defaultContact.whatsapp
      };

      // Merge hours: store-level overrides defaults (partial override supported)
      const storeHours = store.hours || {};
      const hours = {
        weekdays: storeHours.weekdays || defaultHours.weekdays,
        saturday: storeHours.saturday || defaultHours.saturday,
        sunday: storeHours.sunday || defaultHours.sunday,
        // Optional custom day ranges, e.g. [{ days: 'Mon - Thu', time: '10:30 AM - 8:30 PM' }]
        schedule: Array.isArray(storeHours.schedule) ? storeHours.schedule : null
      };

      const merged = {
        ...store,
        email: contact.email,
        phone: contact.phone,
        whatsapp: contact.whatsapp,
        hours: hours
      };

      return merged;
    },

    // ============================================
    // Helper: Generate store label from type
    // ============================================
    getStoreLabel(store, cityName) {
      const labels = this.state.storeTypeLabels || {};
      const storeType = store.store_type || 'flagship';
      let label = labels[storeType] || 'Store in {city}';

      // Replace {city} placeholder
      label = label.replace('{city}', cityName);

      // Add "& Head Office" for headquarters
      if (store.is_headquarters) {
        label = label.replace('Our Flagship Store in', 'Our Flagship Store & Head Office in');
      }

      return label;
    },

    // ============================================
    // Helper: Get amenity label from data or fallback
    // ============================================
    getAmenityLabel(amenityKey) {
      // First check data-driven labels
      if (this.state.amenitiesLabels && this.state.amenitiesLabels[amenityKey]) {
        return this.state.amenitiesLabels[amenityKey];
      }
      // Fallback to hardcoded definitions
      if (this.amenityDefs[amenityKey]) {
        return this.amenityDefs[amenityKey].label;
      }
      // Last resort: format the key
      return amenityKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    createDetailsContent(rawStore, city) {
      // Get merged store data with defaults
      const store = this.getMergedStoreData(rawStore);
      const cityName = city ? city.name : '';
      const storeLabel = this.getStoreLabel(rawStore, cityName);

      let html = '<div class="store-locator__details-content">';

      // Store Header (City & Location Name)
      html += `
        <div class="store-locator__store-header">
          <div class="store-locator__store-label">${escapeHtml(storeLabel)}</div>
          <h3 class="store-locator__store-name">${escapeHtml(store.name)}</h3>
          ${store.landmark ? `<div class="store-locator__store-landmark"><span class="store-locator__landmark-icon">${this.icons.landmark}</span>${escapeHtml(store.landmark)}</div>` : ''}
        </div>
      `;

      // Store Image (if available)
      if (store.storeImage) {
        html += `
          <div class="store-locator__store-image">
            <img src="${escapeHtml(store.storeImage)}" alt="${escapeHtml(store.name)} store" loading="lazy">
          </div>
        `;
      }

      // Address
      html += `
        <div class="store-locator__detail-row">
          <div class="store-locator__detail-icon" aria-hidden="true">${this.icons.location}</div>
          <div class="store-locator__detail-text"><span class="store-locator__detail-label">Address:</span> ${escapeHtml(store.address)}</div>
        </div>
      `;

      // Store Hours
      if (store.hours) {
        const allSameTime = store.hours.weekdays === store.hours.saturday && store.hours.saturday === store.hours.sunday;

        html += `
          <div class="store-locator__hours">
            <div class="store-locator__hours-header">
              <div class="store-locator__detail-icon" aria-hidden="true">${this.icons.clock}</div>
              <span class="store-locator__hours-title">Store Hours</span>
            </div>
            <div class="store-locator__hours-grid">
        `;

        if (store.hours.schedule && store.hours.schedule.length) {
          store.hours.schedule.forEach((slot) => {
            html += `
              <div class="store-locator__hours-row">
                <span class="store-locator__hours-day">${escapeHtml(slot.days)}</span>
                <span class="store-locator__hours-time">${escapeHtml(slot.time)}</span>
              </div>
            `;
          });
        } else if (allSameTime) {
          html += `
              <div class="store-locator__hours-row">
                <span class="store-locator__hours-day">All Days</span>
                <span class="store-locator__hours-time">${escapeHtml(store.hours.weekdays)}</span>
              </div>
          `;
        } else {
          html += `
              <div class="store-locator__hours-row">
                <span class="store-locator__hours-day">Mon - Fri</span>
                <span class="store-locator__hours-time">${escapeHtml(store.hours.weekdays)}</span>
              </div>
              <div class="store-locator__hours-row">
                <span class="store-locator__hours-day">Saturday</span>
                <span class="store-locator__hours-time">${escapeHtml(store.hours.saturday)}</span>
              </div>
              <div class="store-locator__hours-row">
                <span class="store-locator__hours-day">Sunday</span>
                <span class="store-locator__hours-time">${escapeHtml(store.hours.sunday)}</span>
              </div>
          `;
        }

        html += `
            </div>
          </div>
        `;
      }

      // Contact Info Row
      html += '<div class="store-locator__contact-row">';

      // Email
      if (store.email) {
        html += `
          <div class="store-locator__detail-row">
            <div class="store-locator__detail-icon" aria-hidden="true">${this.icons.email}</div>
            <div class="store-locator__detail-text">
              <a href="mailto:${escapeHtml(store.email)}">${escapeHtml(store.email)}</a>
            </div>
          </div>
        `;
      }

      // Phone
      if (store.phone) {
        html += `
          <div class="store-locator__detail-row">
            <div class="store-locator__detail-icon" aria-hidden="true">${this.icons.phone}</div>
            <div class="store-locator__detail-text">
              <a href="tel:${escapeHtml(store.phone.replace(/\s/g, ''))}">${escapeHtml(store.phone)}</a>
            </div>
          </div>
        `;
      }

      html += '</div>';

      // Amenities Cards
      if (store.amenities && store.amenities.length > 0) {
        html += '<div class="store-locator__amenities">';
        html += '<p class="store-locator__amenities-title">Amenities</p>';
        html += '<div class="store-locator__amenities-grid">';

        store.amenities.forEach(amenityKey => {
          const amenityDef = this.amenityDefs[amenityKey];
          const amenityLabel = this.getAmenityLabel(amenityKey);
          const amenityIcon = amenityDef ? amenityDef.icon : this.icons.fallback;

          html += `
            <div class="store-locator__amenity-card">
              <div class="store-locator__amenity-icon" aria-hidden="true">${amenityIcon}</div>
              <span class="store-locator__amenity-label">${escapeHtml(amenityLabel)}</span>
            </div>
          `;
        });

        html += '</div>';
        html += '</div>';
      }

      // Map Preview
      if (store.mapEmbed) {
        html += `
          <div class="store-locator__map-preview">
            <iframe
              src="${escapeHtml(store.mapEmbed)}"
              width="100%"
              height="200"
              style="border:0; border-radius: 8px;"
              allowfullscreen=""
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        `;
      }

      // Action Buttons
      html += '<div class="store-locator__buttons">';

      // Get Directions button
      const gtmStoreId = store.id ? store.id.replace(/-/g, '_') : '';
      html += `
        <a
          href="${escapeHtml(store.directionsUrl || '#')}"
          class="store-locator__btn store-locator__btn--primary"
          target="_blank"
          rel="noopener noreferrer"
          data-gtm-event="${gtmStoreId}_get_direction"
        >
          ${this.icons.map}
          <span>Get Directions</span>
        </a>
      `;

      // Book Appointment button (via WhatsApp)
      if (store.whatsapp) {
        const cityName = city ? city.name : '';
        const appointmentMessage = encodeURIComponent(`Hi, I'd like to book an appointment at your Reia Diamonds ${store.name} store in ${cityName}.`);
        html += `
          <a
            href="https://wa.me/${escapeHtml(store.whatsapp)}?text=${appointmentMessage}"
            class="store-locator__btn store-locator__btn--whatsapp"
            target="_blank"
            rel="noopener noreferrer"
            data-gtm-event="${gtmStoreId}_book_appointment"
            data-reia-wa-store="${escapeHtml(`${store.name}${cityName ? ', ' + cityName : ''}`)}"
          >
            ${this.icons.calendar}
            <span>Book Appointment</span>
          </a>
        `;
      }

      // View Store button
      // Metafield supplies `storePageUrls` (array); fall back to legacy `storePageUrl` (string).
      const storePageUrl = (Array.isArray(store.storePageUrls) && store.storePageUrls[0]) || store.storePageUrl;
      if (storePageUrl) {
        html += `
          <a
            href="${escapeHtml(storePageUrl)}"
            class="store-locator__btn store-locator__btn--secondary"
          >
            <span>View Store</span>
          </a>
        `;
      } else {
        html += `
          <span class="store-locator__btn store-locator__btn--secondary store-locator__btn--disabled">
            View Store
          </span>
        `;
      }

      html += '</div>';
      html += '</div>';

      return html;
    },

    renderError() {
      this.elements.container.classList.add('store-locator--loaded');
      this.elements.citiesList.innerHTML = '<p>Unable to load store locations. Please try again later.</p>';
      this.elements.tabsContainer.innerHTML = '';
      this.elements.detailsContainer.innerHTML = '';
    },

    // ============================================
    // Accessibility
    // ============================================
    announce(message) {
      if (this.elements.announcer) {
        this.elements.announcer.textContent = message;
      }
    }
  };

  // ============================================
  // Initialize on DOM Ready
  // ============================================
  function initStoreLocators() {
    const containers = document.querySelectorAll('.store-locator[data-store-data]');

    containers.forEach(container => {
      // Create a new instance for each container
      const instance = Object.create(StoreLocator);
      instance.state = { ...StoreLocator.state };
      instance.elements = { ...StoreLocator.elements };
      instance.init(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStoreLocators);
  } else {
    initStoreLocators();
  }

})();
